import React, { useEffect, useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { api } from "@/lib/apiClient";
import { CheckCircle2, XCircle, Loader2, Coins } from "lucide-react";

export function PaymentSuccess() {
  const [sp] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = sp.get("session_id");
  const [state, setState] = useState("polling"); // polling | paid | timeout | error
  const [info, setInfo] = useState(null);

  useEffect(() => {
    if (!sessionId) { setState("error"); return; }
    let tries = 0;
    let cancelled = false;
    const poll = async () => {
      if (cancelled) return;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        setInfo(data);
        if (data.payment_status === "paid") { setState("paid"); return; }
        if (data.payment_status === "failed" || data.payment_status === "expired") { setState("error"); return; }
      } catch { /* keep trying */ }
      tries += 1;
      if (tries >= 8) { setState("timeout"); return; }
      setTimeout(poll, 2000);
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId]);

  return (
    <div className="flex min-h-[70vh] items-center justify-center p-4" data-testid="payment-success-page">
      <div className="w-full max-w-md rounded-2xl border border-slate-200 bg-white p-8 text-center shadow-sm">
        {state === "polling" && (
          <>
            <Loader2 className="mx-auto h-10 w-10 animate-spin text-violet-500" />
            <h1 className="mt-4 font-heading text-xl font-bold text-slate-900">Confirmation du paiement…</h1>
            <p className="mt-1 text-sm text-slate-500">Merci de patienter quelques secondes.</p>
          </>
        )}
        {state === "paid" && (
          <>
            <CheckCircle2 className="mx-auto h-12 w-12 text-emerald-500" />
            <h1 className="mt-4 font-heading text-xl font-bold text-slate-900">Paiement confirmé !</h1>
            <p className="mt-1 text-sm text-slate-500">{info?.quantite} jeton{info?.quantite > 1 ? "s" : ""} {info?.vt_type} ajouté{info?.quantite > 1 ? "s" : ""} à votre solde.</p>
            <button data-testid="success-back" onClick={() => navigate("/mes-jetons")} className="mt-6 inline-flex items-center gap-2 rounded-lg bg-violet-600 px-5 py-2.5 text-sm font-semibold text-white hover:bg-violet-700">
              <Coins className="h-4 w-4" /> Voir mes jetons
            </button>
          </>
        )}
        {(state === "timeout" || state === "error") && (
          <>
            <XCircle className="mx-auto h-12 w-12 text-amber-500" />
            <h1 className="mt-4 font-heading text-xl font-bold text-slate-900">{state === "timeout" ? "Traitement en cours" : "Paiement non confirmé"}</h1>
            <p className="mt-1 text-sm text-slate-500">{state === "timeout" ? "Votre paiement est en cours de validation. Vos jetons apparaîtront sous peu." : "Nous n'avons pas pu confirmer ce paiement."}</p>
            <Link to="/mes-jetons" data-testid="success-back" className="mt-6 inline-flex rounded-lg border border-slate-200 px-5 py-2.5 text-sm font-semibold text-slate-700 hover:bg-slate-50">Retour à mes jetons</Link>
          </>
        )}
      </div>
    </div>
  );
}

export function PaymentCancel() {
  return (
    <div className="flex min-h-[70vh] items-center justify-center p-4" data-testid="payment-cancel-page">
      <div className="w-full max-w-md rounded-2xl border border-slate-200 bg-white p-8 text-center shadow-sm">
        <XCircle className="mx-auto h-12 w-12 text-slate-400" />
        <h1 className="mt-4 font-heading text-xl font-bold text-slate-900">Paiement annulé</h1>
        <p className="mt-1 text-sm text-slate-500">Aucun montant n'a été prélevé.</p>
        <Link to="/mes-jetons" data-testid="cancel-back" className="mt-6 inline-flex rounded-lg bg-violet-600 px-5 py-2.5 text-sm font-semibold text-white hover:bg-violet-700">Retour à mes jetons</Link>
      </div>
    </div>
  );
}
